import { domainMeta } from '@/utils/domain-meta';

export type JobPostingReturnToState = {
  returnTo?: string;
};

export function returnToFromRouterState(state: unknown): string | null {
  const returnTo = (state as JobPostingReturnToState | null)?.returnTo;
  return typeof returnTo === 'string' && returnTo.length > 0 ? returnTo : null;
}

export function returnToFromQuery(search: string): string | null {
  const value = new URLSearchParams(search).get('returnTo');
  return value && value.length > 0 ? value : null;
}

export function allowedReturnToOrigins(): string[] {
  return [
    window.location.origin,
    domainMeta.recruiterUiOrigin,
    domainMeta.monolithOrigin,
  ];
}

function isSafeRelativePath(value: string): boolean {
  return (
    value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')
  );
}

export function isAllowedReturnTo(value: string, accountSlug: string): boolean {
  if (value.startsWith('//') || value.includes('\\')) {
    return false;
  }

  if (value.startsWith('/')) {
    return accountSlug.length > 0 && value.includes(`/${accountSlug}/`);
  }

  return safeAbsoluteReturnToHref(value) !== null;
}

/**
 * Rebuilds an absolute Quit URL from an allowlisted origin.
 * The query string may name the path; it cannot name the host or scheme.
 */
export function safeAbsoluteReturnToHref(value: string): string | null {
  if (
    isSafeRelativePath(value) ||
    value.startsWith('//') ||
    value.includes('\\')
  ) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }

  if (parsed.username || parsed.password) {
    return null;
  }

  const trustedOrigin = allowedReturnToOrigins().find(
    origin => origin === parsed.origin,
  );
  if (!trustedOrigin) {
    return null;
  }

  const path = parsed.pathname + parsed.search;
  if (!isSafeRelativePath(path)) {
    return null;
  }

  const safe = new URL(path, trustedOrigin);
  if (safe.origin !== trustedOrigin) {
    return null;
  }

  return safe.href;
}

/**
 * Leaves the posting flow for an allowlisted Hire or Source origin.
 * Rebuilds the href so the query string cannot pick the host or scheme.
 */
export function assignAllowedReturnTo(value: string): boolean {
  const href = safeAbsoluteReturnToHref(value);
  if (!href) {
    return false;
  }

  // Assigns only URLs rebuilt from allowedReturnToOrigins().
  // codeql[js/xss]
  // codeql[js/client-side-unvalidated-url-redirection]
  window.location.assign(href);
  return true;
}

export function resolveReturnTo({
  search,
  state,
  accountSlug,
}: {
  search: string;
  state: unknown;
  accountSlug: string;
}): string | null {
  const candidate = returnToFromRouterState(state) ?? returnToFromQuery(search);
  if (!candidate || !isAllowedReturnTo(candidate, accountSlug)) {
    return null;
  }
  return candidate;
}

export function returnToFromCurrentLocation(
  pathname: string,
  search: string,
  accountSlug: string,
): string | null {
  if (pathname.includes('/post-job')) {
    return null;
  }
  const candidate = `${pathname}${search}`;
  return isAllowedReturnTo(candidate, accountSlug) ? candidate : null;
}
